import { FEET_10_IN_MM, FEET_20_IN_MM } from "../consts";
import { feetToMillimeters, roundDec } from "@tedivo/tedivo-pure-helpers";
import { preCalculateCurrentVesselParts } from "../preCalculateCurrentVesselParts";
export function calculateMissingLcgs({ missingImportantLcgs, allIsoBays, blockBaysAndSizes, blockBaysAndSizesByBlockBay, blockBaysAndSizesBy20Bay, vesselPartsData, containerLengths, }) {
    const missingLcgs = {};
    if (!missingImportantLcgs)
        return { missingLcgs };
    const FEET_40_IN_MM = feetToMillimeters(40);
    const SEPARATION_IN_BETWEEN_BAYS = feetToMillimeters(6);
    const all40Lengths = [40, 45, 48, 53].filter((l) => containerLengths.length === 0 || containerLengths.includes(l));
    const allVesselParts = preCalculateCurrentVesselParts(allIsoBays, vesselPartsData);
    /** Contains the remaining distance of non-bay slots */
    let remainingPartsLength = allVesselParts.reduce((acc, vp) => {
        if (typeof vp === "string")
            return acc;
        return acc + vp.len;
    }, 0);
    /** Total length of bays with separations */
    const totalBaysLength = blockBaysAndSizes.reduce((acc, b) => acc + feetToMillimeters(b.blockSize), (blockBaysAndSizes.length - 1) * SEPARATION_IN_BETWEEN_BAYS);
    let i = 0;
    let remainingBaysLength = totalBaysLength;
    let prevBlock = undefined;
    /** Replace single-paired bays by one double in allVesselParts */
    while (i < allVesselParts.length) {
        const vp = allVesselParts[i];
        if (typeof vp !== "string") {
            i++;
            continue;
        }
        const block = blockBaysAndSizesBy20Bay[vp];
        if ((block === null || block === void 0 ? void 0 : block.numberOfBays) === 2) {
            // Get both bays
            const pBays = block.allBays;
            // Substitute single by double
            allVesselParts[i] = pBays;
            // Find the paired bay
            const bayPaired = pBays.split("-").filter((c) => c !== vp)[0];
            const idx = allVesselParts.findIndex((v) => v === bayPaired);
            // Remove it
            allVesselParts.splice(idx, 1);
            i += 1;
        }
        else {
            i++;
        }
    }
    /** Iterate from 001 to last bay, to obtain LCGs */
    allVesselParts.forEach((vp) => {
        var _a, _b, _c, _d, _e, _f;
        if (typeof vp === "string") {
            const block = blockBaysAndSizesByBlockBay[vp];
            if (!block)
                return;
            const sep = prevBlock === undefined || prevBlock === block.allBays
                ? 0
                : SEPARATION_IN_BETWEEN_BAYS;
            prevBlock = block.allBays;
            if (block.numberOfBays === 2) {
                const blockSize = block.blockSize;
                // As we are adding a separation between 20s, we need to add the separation to the blockSize
                const blockSizeInMm = Math.max(feetToMillimeters(blockSize), FEET_40_IN_MM);
                const fore40Lcg = remainingBaysLength + remainingPartsLength - sep;
                const lcgsInfoOf4x = {
                    foreLcg: fore40Lcg,
                    lcg: fore40Lcg - roundDec(blockSizeInMm * 0.5, 0),
                    aftLcg: fore40Lcg - blockSizeInMm,
                };
                const lcgsInfoOfFore20 = {
                    foreLcg: lcgsInfoOf4x.lcg + FEET_20_IN_MM,
                    lcg: lcgsInfoOf4x.lcg + FEET_10_IN_MM,
                    aftLcg: lcgsInfoOf4x.lcg,
                };
                const lcgsInfoOfAft20 = {
                    foreLcg: lcgsInfoOf4x.lcg,
                    lcg: lcgsInfoOf4x.lcg - FEET_10_IN_MM,
                    aftLcg: lcgsInfoOf4x.lcg - FEET_20_IN_MM,
                };
                const [vpFore, vpAft] = block.allBays
                    .split("-")
                    .sort();
                if (!missingLcgs[vpFore])
                    missingLcgs[vpFore] = new Map();
                if (!missingLcgs[vpAft])
                    missingLcgs[vpAft] = new Map();
                all40Lengths.forEach((l) => {
                    var _a, _b, _c, _d;
                    (_b = (_a = missingLcgs[vpFore]).set) === null || _b === void 0 ? void 0 : _b.call(_a, l, lcgsInfoOf4x);
                    (_d = (_c = missingLcgs[vpAft]).set) === null || _d === void 0 ? void 0 : _d.call(_c, l, lcgsInfoOf4x);
                });
                (_b = (_a = missingLcgs[vpFore]).set) === null || _b === void 0 ? void 0 : _b.call(_a, 20, lcgsInfoOfFore20);
                (_d = (_c = missingLcgs[vpAft]).set) === null || _d === void 0 ? void 0 : _d.call(_c, 20, lcgsInfoOfAft20);
                remainingBaysLength = remainingBaysLength - sep - blockSizeInMm;
            }
            else {
                const blockSize = block.blockSize;
                const blockSizeInMm = feetToMillimeters(blockSize);
                const fore20Lcg = roundDec(remainingBaysLength + remainingPartsLength - sep, 0);
                const mid20Lcg = roundDec(fore20Lcg - blockSizeInMm * 0.5, 0);
                const aft20Lcg = roundDec(fore20Lcg - blockSizeInMm, 0);
                const lcgsInfoOf20 = {
                    foreLcg: fore20Lcg,
                    lcg: mid20Lcg,
                    aftLcg: aft20Lcg,
                };
                if (!missingLcgs[block.base])
                    missingLcgs[block.base] = new Map();
                (_f = (_e = missingLcgs[block.base]).set) === null || _f === void 0 ? void 0 : _f.call(_e, 20, lcgsInfoOf20);
                remainingBaysLength = remainingBaysLength - sep - blockSizeInMm;
            }
        }
        else {
            remainingPartsLength -= vp.len;
        }
    });
    // Calcs the missing 20 ft LCGs
    return {
        missingLcgs: missingLcgs,
    };
}
